import { initializeApp, getApps } from 'firebase/app';
import { getAuth, connectAuthEmulator, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, updateProfile, signOut, sendPasswordResetEmail, sendEmailVerification } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator, httpsCallable } from 'firebase/functions';
const domain = require('../domain/engine.js');
const chatAdapter = require('./chat.js');
const { validateFile } = require('./file-validation.js');

const DEMO_KEY = 'modu-b2b-demo-v1';
const listeners = new Set();
let markerCleanup, watchedMarkerKey, authAttempt, blockedAuthUid;
let refreshNumber = 0, contextGeneration = 0, demoQueue = Promise.resolve();
let session = { user: null, mode: null, companyId: null, snapshot: null, loading: true, error: null, integration: { firebase: 'not_configured', ai: 'not_configured', calendar: 'not_connected', email: 'not_configured' } };
const useFirebaseEmulators = process.env.NODE_ENV !== 'production'
  && process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true'
  && ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
const configuredFirebase = { apiKey: process.env.REACT_APP_FIREBASE_API_KEY, authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN, projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID, storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET, messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID, appId: process.env.REACT_APP_FIREBASE_APP_ID };
const emulatorProjectId = configuredFirebase.projectId || 'demo-b2b-workspace';
// Local SDK placeholders are only available on loopback development hosts with the emulator flag.
const cfg = useFirebaseEmulators ? {
  apiKey: configuredFirebase.apiKey || `demo-key-${emulatorProjectId}`,
  authDomain: configuredFirebase.authDomain || `${emulatorProjectId}.firebaseapp.com`,
  projectId: emulatorProjectId,
  storageBucket: configuredFirebase.storageBucket || `${emulatorProjectId}.appspot.com`,
  messagingSenderId: configuredFirebase.messagingSenderId || '123456789',
  appId: configuredFirebase.appId || '1:123456789:web:emulator',
} : configuredFirebase;
let auth, db, functions;
const key = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
function publish(patch) {
  if (('user' in patch && patch.user?.uid !== session.user?.uid) || ('companyId' in patch && patch.companyId !== session.companyId) || ('mode' in patch && patch.mode !== session.mode)) contextGeneration++;
  session = { ...session, ...patch }; listeners.forEach(fn => fn(session));
}
function originGuard() {
  const generation = contextGeneration;
  return () => { if (generation !== contextGeneration) throw Object.assign(new Error('현재 계정 또는 기업이 변경되었습니다. 이전 기업의 실행 결과는 해당 기업에서 다시 확인해 주세요.'), { code: 'CONTEXT_CHANGED' }); };
}
function currentContext() {
  if (!session.user) throw Object.assign(new Error('먼저 로그인해 주세요.'), { code: 'UNAUTHENTICATED' });
  return { uid: session.user.uid, companyId: session.companyId };
}
function friendly(error, provider) {
  const code = String(error.code || 'UNKNOWN').replace(/^functions\//, '');
  if (code.startsWith('auth/')) {
    const messages = {
      'auth/popup-closed-by-user': 'Google 로그인을 취소했습니다.',
      'auth/cancelled-popup-request': 'Google 로그인이 취소되었습니다. 다시 시도해 주세요.',
      'auth/popup-blocked': '브라우저가 로그인 팝업을 차단했습니다. 이 사이트의 팝업을 허용한 뒤 다시 시도해 주세요.',
      'auth/operation-not-allowed': provider === 'google' ? '현재 Google 로그인을 사용할 수 없습니다. 관리자에게 문의해 주세요.' : '현재 이 로그인 방식을 사용할 수 없습니다. 관리자에게 문의해 주세요.',
      'auth/unauthorized-domain': '이 사이트에서 Google 로그인을 사용할 수 없습니다. 관리자에게 문의해 주세요.',
      'auth/account-exists-with-different-credential': '이 이메일은 다른 로그인 방식으로 등록되어 있습니다. 기존 로그인 방식을 사용해 주세요.',
      'auth/credential-already-in-use': '이미 다른 계정에 연결된 로그인 정보입니다. 기존 로그인 방식을 사용해 주세요.',
      'auth/network-request-failed': '인증 서버에 연결하지 못했습니다. 인터넷 연결을 확인한 뒤 다시 시도해 주세요.',
      'auth/user-disabled': '사용이 중지된 계정입니다. 관리자에게 문의해 주세요.',
      'auth/too-many-requests': '로그인 시도가 많습니다. 잠시 후 다시 시도해 주세요.',
      'auth/invalid-credential': '이메일 또는 비밀번호를 확인해 주세요.',
      'auth/wrong-password': '이메일 또는 비밀번호를 확인해 주세요.',
      'auth/user-not-found': '이메일 또는 비밀번호를 확인해 주세요.',
      'auth/invalid-email': '올바른 이메일 주소를 입력해 주세요.',
      'auth/email-already-in-use': '이미 가입된 이메일입니다. 기존 계정으로 로그인해 주세요.',
      'auth/weak-password': '더 안전한 비밀번호를 입력해 주세요.',
      'auth/invalid-api-key': '현재 로그인 서비스를 사용할 수 없습니다. 관리자에게 문의해 주세요.',
      'auth/operation-not-supported-in-this-environment': '이 브라우저 환경에서 로그인 팝업을 사용할 수 없습니다. 일반 브라우저에서 열어 주세요.',
    };
    return Object.assign(new Error(messages[code] || '로그인을 완료하지 못했습니다. 다시 시도해 주세요.'), { code });
  }
  const message = /unavailable|not-found|internal/.test(code) ? 'Firebase 서버에 연결할 수 없습니다. Functions 배포 또는 에뮬레이터 실행 상태를 확인해 주세요.' : error.message || '요청을 처리하지 못했습니다.';
  return Object.assign(new Error(message), { code, details: error.details });
}
async function call(name, data) {
  if (!functions) throw new Error('Firebase 환경 변수를 설정한 후 개발 서버를 다시 시작해 주세요.');
  try { return (await httpsCallable(functions, name, { timeout: 180000 })(data)).data; } catch (error) { throw friendly(error); }
}
function readDemo() {
  const stored = localStorage.getItem(DEMO_KEY);
  if (stored) return JSON.parse(stored);
  return domain.createDemoState();
}
function myCompanyIds(state) {
  const members = Array.isArray(state.members) ? state.members : Object.values(state.members || state.companyMembers || {});
  return members.filter(m => m.uid === session.user?.uid && m.status === 'ACTIVE').map(m => m.companyId);
}
function withChats(snapshot, state, ctx) {
  const threads = (state.threads || []).filter(t => t.companyId === ctx.companyId && (t.createdBy === ctx.uid || t.uid === ctx.uid));
  const ids = new Set(threads.map(t => t.id));
  return { ...snapshot, threads, messages: (state.messages || []).filter(m => ids.has(m.threadId)), integration: snapshot.integration || session.integration };
}
async function refresh() {
  if (!session.user) return;
  const number = ++refreshNumber, uid = session.user.uid, mode = session.mode, requestedCompanyId = session.companyId;
  const isCurrent = () => number === refreshNumber && uid === session.user?.uid && mode === session.mode && requestedCompanyId === session.companyId;
  try {
    let snapshot, companyId = requestedCompanyId, integration = session.integration;
    if (mode === 'demo') {
      const state = readDemo();
      if (!companyId) companyId = myCompanyIds(state)[0] || null;
      const ctx = { uid, companyId };
      snapshot = withChats(domain.getSnapshot(state, ctx), state, ctx);
    } else {
      const data = await call('workspaceSnapshot', { companyId: requestedCompanyId });
      snapshot = data.snapshot;
      if (!companyId && data.companyId) companyId = data.companyId;
      if (data.integration) integration = data.integration;
    }
    if (isCurrent()) { publish({ snapshot, companyId, integration, loading: false, error: null }); watchMarker(); }
    return snapshot;
  } catch (error) { if (isCurrent()) publish({ loading: false, ...(/permission-denied|PERMISSION_DENIED/.test(String(error.code)) ? { snapshot: null } : {}), error: friendly(error).message }); throw error; }
}
function stopMarker() { markerCleanup?.(); markerCleanup = null; watchedMarkerKey = null; }
function watchMarker() {
  const next = `${session.mode}:${session.user?.uid}:${session.companyId}`;
  if (next === watchedMarkerKey) return;
  markerCleanup?.(); markerCleanup = null; watchedMarkerKey = next;
  if (session.mode === 'firebase' && session.user && session.companyId) {
    let first = true;
    markerCleanup = onSnapshot(doc(db, 'companyUpdates', session.companyId, 'members', session.user.uid), () => {
      if (next !== watchedMarkerKey) return;
      if (first) { first = false; return; }
      refresh().catch(() => {});
    }, error => { if (next === watchedMarkerKey) publish({ error: friendly(error).message }); });
  }
}
function safeAuthUser(user) {
  return user ? { uid: user.uid, email: user.email, displayName: user.displayName, emailVerified: user.emailVerified } : null;
}
function adoptAuthUser(user) {
  stopMarker(); ++refreshNumber;
  publish({ user: safeAuthUser(user), mode: user ? 'firebase' : null, companyId: user ? localStorage.getItem(`modu-company-${user.uid}`) : null, snapshot: null, chatProgress: null, loading: !!user, error: null, integration: { ...session.integration, firebase: useFirebaseEmulators ? 'emulator' : 'real', ai: 'unknown' } });
}
async function restoreFirebaseWorkspace(user) {
  const selectedCompanyId = session.companyId;
  const generation = contextGeneration;
  try { return await refresh(); } catch (error) {
    // A saved selection is only a hint; the server must resolve current memberships.
    if (!selectedCompanyId || !/permission-denied|PERMISSION_DENIED/.test(String(error.code)) || generation !== contextGeneration || session.mode !== 'firebase' || session.user?.uid !== user.uid) throw error;
    localStorage.removeItem(`modu-company-${user.uid}`);
    publish({ companyId: null, snapshot: null, loading: true, error: null });
    return refresh();
  }
}
function changedAuthentication() {
  return Object.assign(new Error('로그인 중 계정 화면이 변경되어 이전 로그인 요청을 취소했습니다. 다시 로그인해 주세요.'), { code: 'CONTEXT_CHANGED' });
}
function beginAuthentication(provider) {
  if (!auth) throw new Error('로그인 서비스 설정이 필요합니다. 관리자에게 문의해 주세요.');
  if (authAttempt) throw Object.assign(new Error('로그인이 이미 진행 중입니다. 현재 로그인 창을 완료해 주세요.'), { code: 'AUTH_IN_PROGRESS' });
  const attempt = { provider, previous: session, cancelled: false };
  authAttempt = attempt; blockedAuthUid = null; stopMarker(); ++refreshNumber;
  publish({ user: null, mode: null, companyId: null, snapshot: null, chatProgress: null, loading: true, error: null });
  return attempt;
}
function cancelAuthentication() { if (authAttempt) authAttempt.cancelled = true; }
function observeExternalAuthChange(attempt, expectedUser) {
  if (!expectedUser || auth.currentUser?.uid === expectedUser.uid || attempt.cancelled) return;
  attempt.cancelled = true;
  if (session.mode !== 'demo') {
    adoptAuthUser(auth.currentUser);
    if (auth.currentUser) restoreFirebaseWorkspace(auth.currentUser).catch(() => {});
  }
}
function assertAuthentication(attempt, expectedUser) {
  observeExternalAuthChange(attempt, expectedUser);
  if (authAttempt !== attempt || attempt.cancelled) throw changedAuthentication();
}
async function authenticate(provider, start, afterCredential) {
  const attempt = beginAuthentication(provider);
  let credential, adopted = false;
  try {
    // Keep this call synchronous with the click; awaiting before it can block the popup.
    credential = await start();
    attempt.authenticatedUser = credential.user;
    assertAuthentication(attempt, credential.user);
    if (afterCredential) { await afterCredential(credential); assertAuthentication(attempt, credential.user); }
    adoptAuthUser(credential.user); adopted = true;
    await restoreFirebaseWorkspace(credential.user);
    assertAuthentication(attempt, credential.user);
    return { user: safeAuthUser(credential.user), providerId: credential.providerId || null };
  } catch (error) {
    if (attempt.cancelled) {
      if (credential?.user) {
        blockedAuthUid = credential.user.uid;
        if (auth.currentUser?.uid === credential.user.uid) await signOut(auth).catch(() => {});
      }
      throw changedAuthentication();
    }
    const problem = friendly(error, provider);
    if (credential?.user) {
      // Auth may succeed before profile or workspace access fails. Keep that identity.
      if (!adopted) { adoptAuthUser(credential.user); await restoreFirebaseWorkspace(credential.user).catch(() => {}); }
      assertAuthentication(attempt, credential.user);
      publish({ loading: false, error: problem.message });
    } else if (attempt.previous.mode === 'demo' || (attempt.previous.mode === 'firebase' && auth.currentUser?.uid === attempt.previous.user?.uid)) {
      publish({ ...attempt.previous, ...(attempt.previous.mode === 'firebase' ? { user: safeAuthUser(auth.currentUser) } : {}), chatProgress: null, loading: false, error: problem.message }); watchMarker();
    } else if (auth.currentUser && auth.currentUser.uid !== blockedAuthUid) {
      adoptAuthUser(auth.currentUser); await restoreFirebaseWorkspace(auth.currentUser).catch(() => {});
      assertAuthentication(attempt); publish({ loading: false, error: problem.message });
    } else publish({ user: null, mode: null, companyId: null, snapshot: null, chatProgress: null, loading: false, error: problem.message });
    throw problem;
  } finally { if (authAttempt === attempt) authAttempt = null; }
}
async function demoTransaction(fn) {
  const work = async () => {
    const state = readDemo();
    const next = JSON.parse(JSON.stringify(state));
    const value = await fn(next);
    localStorage.setItem(DEMO_KEY, JSON.stringify(next));
    await refresh();
    return value;
  };
  const task = demoQueue.then(() => navigator.locks ? navigator.locks.request(DEMO_KEY, work) : work());
  demoQueue = task.catch(() => {});
  return task;
}
if (cfg.apiKey && cfg.projectId && cfg.appId) {
  const app = getApps()[0] || initializeApp(cfg);
  auth = getAuth(app); db = getFirestore(app); functions = getFunctions(app, process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || 'asia-northeast3');
  if (useFirebaseEmulators) {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, '127.0.0.1', 8080); connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  }
  onAuthStateChanged(auth, user => {
    if (authAttempt) { observeExternalAuthChange(authAttempt, authAttempt.authenticatedUser); return; }
    if (session.mode === 'demo' || (user && user.uid === blockedAuthUid)) return;
    // Queued callbacks from an earlier Auth transition cannot restore an old account.
    if (user?.uid !== auth.currentUser?.uid) return;
    if (user && session.mode === 'firebase' && session.user?.uid === user.uid) {
      publish({ user: safeAuthUser(user) }); return;
    }
    adoptAuthUser(user);
    if (user) restoreFirebaseWorkspace(user).catch(() => {});
  });
} else { session.loading = false; }
window.addEventListener('storage', event => { if (event.key === DEMO_KEY && session.mode === 'demo') refresh().catch(() => {}); });

export const api = {
  getSession: () => session,
  subscribe(listener) { listeners.add(listener); listener(session); return () => listeners.delete(listener); },
  async login(email, password) {
    return authenticate('password', () => signInWithEmailAndPassword(auth, email, password));
  },
  async loginWithGoogle() {
    return authenticate('google', () => {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      auth.languageCode = 'ko';
      return signInWithPopup(auth, provider);
    });
  },
  async signup(email, password, name) {
    return authenticate('password', () => createUserWithEmailAndPassword(auth, email, password), credential => updateProfile(credential.user, { displayName: name }));
  },
  async logout() {
    cancelAuthentication(); stopMarker(); ++refreshNumber;
    if (auth?.currentUser) blockedAuthUid = auth.currentUser.uid;
    publish({ user: null, mode: null, companyId: null, snapshot: null, chatProgress: null, error: null, loading: false });
    if (auth) await signOut(auth);
  },
  async resetPassword(email) { if (!auth) throw new Error('Firebase 설정이 필요합니다.'); await sendPasswordResetEmail(auth, email); },
  async sendVerification() { if (!auth?.currentUser) throw new Error('먼저 로그인해 주세요.'); await sendEmailVerification(auth.currentUser); },
  async refreshAuth() {
    if (!auth?.currentUser || session.mode !== 'firebase') return refresh();
    await auth.currentUser.reload(); await auth.currentUser.getIdToken(true);
    publish({ user: { uid: auth.currentUser.uid, email: auth.currentUser.email, displayName: auth.currentUser.displayName, emailVerified: auth.currentUser.emailVerified } });
    return refresh();
  },
  async acceptInvitation(memberId, companyId) {
    const ctx = currentContext(), guard = originGuard();
    if (session.mode === 'demo') return demoTransaction(state => {
      const result = domain.executeOperation(state, { ...ctx, companyId }, 'manage_company_members', { action: 'ACCEPT', memberId }, { verifiedEmail: session.user.email, executionKey: key() });
      Object.assign(state, result.state); session.companyId = companyId; return result.result;
    });
    const data = await call('workspaceOperation', { companyId, tool: 'manage_company_members', args: { action: 'ACCEPT', memberId }, meta: { executionKey: key(), source: 'screen' } });
    guard();
    publish({ companyId, snapshot: data.snapshot, error: null }); await refresh(); return data.result;
  },
  async enterDemo() {
    cancelAuthentication(); stopMarker(); ++refreshNumber;
    const state = readDemo();
    const user = Array.isArray(state.users) ? state.users.find(u => u.uid === 'demo-user' || u.id === 'demo-user') || state.users[0] : Object.values(state.users || {})[0];
    publish({ user: { uid: user?.uid || user?.id || 'demo-user', email: user?.email || 'demo@example.test', displayName: user?.name || '김민준' }, mode: 'demo', companyId: null, snapshot: null, chatProgress: null, loading: true, error: null, integration: { firebase: 'local_demo', ai: 'mock', email: 'not_configured', calendar: 'not_connected' } });
    if (!localStorage.getItem(DEMO_KEY)) localStorage.setItem(DEMO_KEY, JSON.stringify(state));
    await refresh();
  },
  async resetDemo() { localStorage.removeItem(DEMO_KEY); return api.enterDemo(); },
  async switchCompany(companyId) {
    currentContext(); ++refreshNumber;
    publish({ companyId, snapshot: null, loading: true, error: null });
    if (session.mode === 'firebase') localStorage.setItem(`modu-company-${session.user.uid}`, companyId);
    return refresh();
  },
  refresh,
  clearError() { publish({ error: null }); },
  async execute(tool, args = {}, meta = {}) {
    const ctx = currentContext(), guard = originGuard(), operationMeta = { executionKey: meta.executionKey || meta.idempotencyKey || key(), source: meta.source === 'chat' ? 'chat' : 'screen' };
    if (session.mode === 'demo') return demoTransaction(state => {
      if (tool === 'extract_document_fields' && args.fileId) {
        domain.executeOperation(state, ctx, 'get_file_reference', { fileId: args.fileId }, {});
        const file = state.files.find(f => f.id === args.fileId);
        if (!file?.extractedText) throw new Error('PDF/OCR 자동 추출은 연결되지 않았습니다. TXT/CSV 자료 또는 확인한 원문을 입력해 주세요.');
        args = { ...args, text: file.extractedText };
      }
      const outcome = domain.executeOperation(state, ctx, tool, JSON.parse(JSON.stringify(args)), operationMeta);
      Object.assign(state, outcome.state);
      if (tool === 'create_company') session.companyId = outcome.result.data.id;
      return outcome.result;
    });
    const data = await call('workspaceOperation', { companyId: ctx.companyId, tool, args, meta: operationMeta });
    guard();
    if (data.snapshot) publish({ snapshot: data.snapshot, companyId: data.snapshot.context?.companyId || session.companyId, error: null });
    else await refresh();
    return data.result;
  },
  async createThread(title = '새 대화', context = {}) {
    const ctx = currentContext(), guard = originGuard();
    if (session.mode === 'demo') return demoTransaction(state => chatAdapter.createThread(state, ctx, title, context));
    const data = await call('workspaceThread', { companyId: ctx.companyId, title, context });
    guard();
    await refresh(); return data.thread;
  },
  async chat(text, options = {}) {
    const ctx = currentContext(), guard = originGuard(), runId = options.runId || key();
    if (session.mode === 'demo') return demoTransaction(async state => chatAdapter.runMockChat(state, ctx, text, { ...options, runId }, domain));
    const callable = httpsCallable(functions, 'workspaceChat', { timeout: 180000 });
    let data;
    try {
      if (typeof callable.stream === 'function') {
        const response = await callable.stream({ companyId: ctx.companyId, text, threadId: options.threadId || null, context: options.context || {}, fileIds: options.fileIds || [], runId });
        for await (const chunk of response.stream) {
          guard();
          options.onChunk?.(chunk); publish({ chatProgress: chunk });
        }
        data = await response.data;
      } else data = (await callable({ companyId: ctx.companyId, text, threadId: options.threadId || null, context: options.context || {}, fileIds: options.fileIds || [], runId })).data;
    } catch (error) { await refresh().catch(() => {}); throw friendly(error); }
    guard(); await refresh(); publish({ chatProgress: null }); return data;
  },
  async uploadFile(file, link = {}) {
    const ctx = currentContext(), guard = originGuard(), bytes = new Uint8Array(await file.arrayBuffer()); guard();
    const checked = validateFile(bytes, file.type, file.name);
    let base64 = ''; for (let i = 0; i < bytes.length; i += 8192) base64 += String.fromCharCode(...bytes.slice(i, i + 8192));
    base64 = btoa(base64);
    if (session.mode === 'demo') return demoTransaction(state => {
      const fileId = key();
      const storagePath = `companies/${ctx.companyId}/private/${fileId}`;
      const outcome = domain.executeOperation(state, ctx, 'attach_file', { name: checked.name, mimeType: checked.mimeType, size: checked.size, resourceType: link.resourceType || 'company', resourceId: link.resourceId || ctx.companyId, sharedWith: link.sharedWith || [], storagePath }, { executionKey: fileId, source: 'screen', trustedFile: true, fileVerified: true });
      Object.assign(state, outcome.state); state.demoFileBytes ||= {}; state.demoFileBytes[storagePath] = base64;
      state.files.find(f => f.storagePath === storagePath).extractedText = checked.extractedText;
      return outcome.result.data;
    });
    const data = await call('workspaceUpload', { companyId: ctx.companyId, name: file.name, mimeType: file.type, base64, link });
    guard();
    await refresh(); return data.result.data;
  },
  async downloadFile(fileId) {
    const ctx = currentContext(), guard = originGuard(); let data;
    if (session.mode === 'demo') {
      const state = readDemo(); const result = domain.executeOperation(state, ctx, 'get_file_reference', { fileId }, {}).result;
      const file = result.data?.file || result.data || result.file;
      const base64 = state.demoFileBytes?.[file?.storagePath];
      if (!base64) throw new Error('시연 파일은 업로드한 브라우저에서만 다운로드할 수 있습니다.');
      data = { base64, name: file.name, mimeType: file.mimeType };
    } else data = await call('workspaceDownload', { companyId: ctx.companyId, fileId });
    guard(); const bytes = Uint8Array.from(atob(data.base64), c => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: data.mimeType }));
    const anchor = document.createElement('a'); anchor.href = url; anchor.download = data.name; anchor.click(); setTimeout(() => URL.revokeObjectURL(url), 10000);
    return data;
  }
};
export default api;
